import numberFormatter from '../../utils/formatNumber';
import { Box, Tooltip, Typography } from '@mui/material';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import {
  VALUE_CLASS_COMPOSITION_EMPTY_MESSAGE,
  VALUE_CLASS_COMPOSITION_LABEL,
  VALUE_CLASS_HIGH_FULL_LABEL,
  VALUE_CLASS_HIGH_LABEL,
  VALUE_CLASS_LOW_FULL_LABEL,
  VALUE_CLASS_LOW_LABEL,
  VALUE_CLASS_MID_FULL_LABEL,
  VALUE_CLASS_MID_LABEL,
} from '../../content';
import type { ValueClassCompositionPreview } from '../../types/modelScoring';

export const VALUE_CLASS_BAR_COLORS = {
  high: '#413762',
  mid: '#9e8fc9',
  low: '#dcd7ea',
} as const;

const TEXT_PRIMARY = '#1a1a1a';
const TEXT_MUTED = '#685d6f';
const APPROX_COLOR = '#413762';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      marginTop: theme.spacing(2),
    },
    headerRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'baseline',
      flexWrap: 'wrap',
      gap: theme.spacing(1),
      marginBottom: theme.spacing(1.5),
    },
    title: {
      '&&': {
        fontFamily: 'Calibre-Semibold,sans-serif',
        fontWeight: 600,
        fontSize: 16,
        color: TEXT_PRIMARY,
      },
    },
    approxSize: {
      '&&': {
        fontFamily: 'Calibre-Regular,sans-serif',
        fontSize: 16,
        color: APPROX_COLOR,
      },
    },
    barSection: {
      width: '100%',
    },
    barLabelsRow: {
      display: 'flex',
      width: '100%',
      marginBottom: theme.spacing(0.75),
      minHeight: 20,
    },
    barLabelCell: {
      flexShrink: 0,
      paddingRight: theme.spacing(0.5),
      overflow: 'hidden',
    },
    barLabelText: {
      '&&': {
        fontFamily: 'Calibre-Regular,sans-serif',
        fontSize: 14,
        lineHeight: 1.25,
        color: TEXT_PRIMARY,
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        display: 'block',
        fontWeight: 700,
      },
    },
    barTrack: {
      display: 'flex',
      height: 35,
      borderRadius: theme.spacing(1),
      overflow: 'hidden',
      width: '100%',
    },
    barSegment: {
      flexShrink: 0,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
    legendRow: {
      display: 'flex',
      justifyContent: 'flex-start',
      marginTop: theme.spacing(1.5),
      flexWrap: 'wrap',
      gap: theme.spacing(1),
    },
    legendItem: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
    },
    legendDot: {
      width: 10,
      height: 10,
      borderRadius: '50%',
      flexShrink: 0,
    },
    legendText: {
      '&&': {
        fontFamily: 'Calibre-Regular,sans-serif',
        fontSize: 14,
        color: TEXT_MUTED,
      },
    },
    emptyState: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 35,
      padding: theme.spacing(1.5, 2),
      borderRadius: theme.spacing(1),
      backgroundColor: '#f5f3f8',
      border: `1px dashed ${TEXT_MUTED}`,
      width: '100%',
    },
    emptyStateText: {
      '&&': {
        fontFamily: 'Calibre-Regular,sans-serif',
        fontSize: 14,
        lineHeight: 1.35,
        color: TEXT_MUTED,
        textAlign: 'center',
      },
    },
  })
);

export interface ValueClassCompositionBarProps {
  data: ValueClassCompositionPreview;
}

const pctLabel = (fraction: number) => `${Math.round(fraction * 100)}%`;

const isCompositionEmpty = (d: ValueClassCompositionPreview) => {
  const sum = d.high_pct + d.medium_pct + d.low_pct;
  return sum < Number.EPSILON;
};

const ValueClassCompositionBar = ({ data }: ValueClassCompositionBarProps) => {
  const classes = useStyles();
  const approxSize = `~${numberFormatter(data.total_count, 1)} users`;
  const empty = isCompositionEmpty(data);

  const segments = [
    {
      key: 'high',
      label: VALUE_CLASS_HIGH_LABEL,
      fullLabel: VALUE_CLASS_HIGH_FULL_LABEL,
      pct: data.high_pct,
      color: VALUE_CLASS_BAR_COLORS.high,
    },
    {
      key: 'mid',
      label: VALUE_CLASS_MID_LABEL,
      fullLabel: VALUE_CLASS_MID_FULL_LABEL,
      pct: data.medium_pct,
      color: VALUE_CLASS_BAR_COLORS.mid,
    },
    {
      key: 'low',
      label: VALUE_CLASS_LOW_LABEL,
      fullLabel: VALUE_CLASS_LOW_FULL_LABEL,
      pct: data.low_pct,
      color: VALUE_CLASS_BAR_COLORS.low,
    },
  ] as const;

  return (
    <Box className={classes.root}>
      <Box className={classes.headerRow}>
        <Typography className={classes.title}>
          {VALUE_CLASS_COMPOSITION_LABEL}
        </Typography>
        <Typography className={classes.approxSize}>{approxSize}</Typography>
      </Box>

      <Box className={classes.barSection}>
        {empty ? (
          <Box className={classes.emptyState}>
            <Typography className={classes.emptyStateText}>
              {VALUE_CLASS_COMPOSITION_EMPTY_MESSAGE}
            </Typography>
          </Box>
        ) : (
          <>
            <Box className={classes.barTrack}>
              {segments.map((s, i) => {
                const content = `${s.label} - ${pctLabel(s.pct)}`;
                return (
                  <Tooltip title={content} key={s.key}>
                    <Box
                      className={classes.barSegment}
                      style={{
                        width: `${s.pct * 100}%`,
                        backgroundColor: s.color,
                      }}
                    >
                      <Typography
                        className={classes.barLabelText}
                        style={i === 0 ? { color: 'white' } : {}}
                      >
                        {content}
                      </Typography>
                    </Box>
                  </Tooltip>
                );
              })}
            </Box>

            <Box className={classes.legendRow}>
              {segments.map((s) => (
                <Box key={`legend-${s.key}`} className={classes.legendItem}>
                  <Box
                    className={classes.legendDot}
                    style={{ backgroundColor: s.color }}
                  />
                  <Typography className={classes.legendText}>
                    {s.fullLabel}
                  </Typography>
                </Box>
              ))}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default ValueClassCompositionBar;
